import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import api from '../../services/api';

/**
 * Team365 Money (2026-10-06): one place for 365reset's bookkeeping. Stripe
 * activity comes in with one button, partner payouts are recorded when Jenn
 * sends them, anything else is typed in, and everything goes to the
 * "365reset Bookkeeping" Google Sheet's Journal tab. Jenn sends money
 * herself; this only records it.
 */

const fmt = (n) => (Number(n) || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Halifax' });
const thisMonth = () => today().slice(0, 7);
const monthName = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
const shiftMonth = (m, d) => { const [y, mo] = m.split('-').map(Number); const dt = new Date(Date.UTC(y, mo - 1 + d, 1)); return dt.toISOString().slice(0, 7); };

// Jenn, 2026-10-06: everything is paid personally for now; Stripe is the
// only thing in 365reset's name. "365reset bank account" is for once her
// dedicated account starts (before 2027).
const PAID_FROM = [
  { value: 'personal', label: 'Personally (your own account or card)' },
  { value: 'stripe', label: 'Stripe (365reset account)' },
  { value: 'stripePrev', label: 'Stripe (previous account)' },
  { value: 'bank', label: '365reset bank account' },
];
const input = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta';
const label = 'block text-xs font-medium text-gray-500 mb-1';
const btn = 'px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50';
const btnQuiet = 'px-3 py-1.5 border border-gray-200 text-reset-brown text-sm rounded-lg hover:bg-reset-creamDark disabled:opacity-50';

function Card({ title, sub, children }) {
  return (
    <section className="bg-white rounded-2xl shadow-sm p-5 mb-4">
      <h2 className="text-base font-semibold text-reset-brown">{title}</h2>
      {sub && <p className="text-sm text-gray-500 mb-3">{sub}</p>}
      {children}
    </section>
  );
}

function Stat({ name, value }) {
  return (
    <div className="bg-reset-cream rounded-xl p-3">
      <p className="text-xs text-gray-500">{name}</p>
      <p className="text-lg font-semibold text-reset-brown">{value}</p>
    </div>
  );
}

export default function AdminMoney() {
  const [params, setParams] = useSearchParams();
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/money', { params: { month } })
      .then(r => setData(r.data))
      .catch(err => setMsg(err.response?.data?.error || 'Could not load this month.'));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data?.connected && !accounts) api.get('/admin/money/accounts').then(r => setAccounts(r.data.accounts)).catch(() => {});
  }, [data, accounts]);

  useEffect(() => {
    if (params.get('sheets_connected')) setMsg('Google Sheets is connected. Entries can now go to your bookkeeping spreadsheet.');
    if (params.get('sheets_error')) setMsg("Google Sheets didn't connect. Try the button again.");
    if (params.get('sheets_connected') || params.get('sheets_error')) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (key, fn, done) => {
    setBusy(key); setMsg(null);
    try { const r = await fn(); if (done) setMsg(done(r)); load(); }
    catch (err) { setMsg(err.response?.data?.error || 'That did not work. Try again.'); }
    finally { setBusy(null); }
  };

  const connect = async () => {
    const r = await api.get('/admin/money/connect-url');
    window.location.href = r.data.url;
  };

  const d = data;
  const waiting = d ? d.entries.filter(e => !e.synced_at).length : 0;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <AdminNav />
        <h1 className="font-serif text-3xl font-bold text-reset-brown mb-1">Money</h1>
        <p className="text-sm text-gray-500 mb-4">
          Record what comes in and goes out. Everything goes to your{' '}
          {d ? <a className="text-reset-terracotta font-semibold underline" href={d.sheetUrl} target="_blank" rel="noopener noreferrer">365reset Bookkeeping spreadsheet</a> : 'bookkeeping spreadsheet'}.
          You send the money; this keeps the record.
        </p>

        {msg && <p role="status" className="bg-reset-creamDark text-reset-brown rounded-xl px-4 py-3 text-sm mb-4">{msg}</p>}

        <div className="flex items-center gap-2 mb-4">
          <button type="button" className={btnQuiet} onClick={() => setMonth(m => shiftMonth(m, -1))} aria-label="Previous month">‹</button>
          <p className="text-lg font-semibold text-reset-brown min-w-[11rem] text-center">{monthName(month)}</p>
          <button type="button" className={btnQuiet} onClick={() => setMonth(m => shiftMonth(m, 1))} aria-label="Next month">›</button>
        </div>

        {!d ? <p className="text-sm text-gray-500">Loading…</p> : (
          <>
            {!d.connected && (
              <Card title="Connect your spreadsheet" sub="One time only. Google will ask you to allow 365reset to write to your Google Sheets.">
                <button type="button" className={btn} onClick={connect}>Connect Google Sheets</button>
              </Card>
            )}

            <Card title="Month-end check" sub="Everything should agree before the month is closed.">
              <ul className="text-sm space-y-1.5 text-reset-brown">
                <li>{d.check.stripeMatches ? '✓' : '•'} Stripe payments {fmt(d.check.stripeGross)}, recorded {fmt(d.check.recordedGross)}{d.check.stripeMatches ? '' : ': bring in this month’s Stripe activity below'}</li>
                <li>{d.check.waitingForSheet === 0 ? '✓' : '•'} {d.check.waitingForSheet === 0 ? 'Everything is in the spreadsheet' : `${d.check.waitingForSheet} ${d.check.waitingForSheet === 1 ? 'entry is' : 'entries are'} waiting to go to the spreadsheet`}</li>
                <li>{d.check.unbalanced === 0 ? '✓' : '•'} {d.check.unbalanced === 0 ? 'Every entry balances' : `${d.check.unbalanced} ${d.check.unbalanced === 1 ? 'entry doesn’t' : 'entries don’t'} balance`}</li>
                {d.check.sheetDifference !== null && (
                  <li>{Number(d.check.sheetDifference) === 0 ? '✓' : '•'} {Number(d.check.sheetDifference) === 0 ? 'The spreadsheet’s trial balance balances' : `The spreadsheet’s trial balance is off by ${fmt(d.check.sheetDifference)}`}</li>
                )}
                {d.partners.some(p => p.owedNow > 0) && <li>• {d.partners.filter(p => p.owedNow > 0).length} partner payout{d.partners.filter(p => p.owedNow > 0).length === 1 ? '' : 's'} owed (see Partners)</li>}
              </ul>
            </Card>

            <Card title="From Stripe" sub="The 365reset Stripe account, this month.">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <Stat name="Payments" value={fmt(d.stripe.payments)} />
                <Stat name="Stripe fees" value={fmt(d.stripe.fees)} />
                <Stat name="Refunds" value={fmt(d.stripe.refunds)} />
                <Stat name="Deposited to bank" value={fmt(d.stripe.deposits)} />
              </div>
              <div className="mb-3">
                <label className={label} htmlFor="deposit-to">Stripe deposits land in</label>
                <select id="deposit-to" className={`${input} sm:w-auto`} value={d.depositTo}
                  onChange={e => run('settings', () => api.put('/admin/money/settings', { depositTo: e.target.value }), () => 'Saved. New Stripe deposits will be recorded that way.')}>
                  <option value="personal">My personal account (counts as money I took out)</option>
                  <option value="bank">365reset's own bank account</option>
                </select>
              </div>
              <button type="button" className={btn} disabled={busy === 'stripe'}
                onClick={() => run('stripe', () => api.post('/admin/money/stripe-import', { month }), r => (r.data.added ? `Brought in ${r.data.added} new Stripe ${r.data.added === 1 ? 'entry' : 'entries'}.` : 'Nothing new from Stripe this month.'))}>
                {busy === 'stripe' ? 'Bringing in…' : 'Bring in this month’s Stripe activity'}
              </button>
              <p className="text-xs text-gray-400 mt-2">Safe to press more than once. Nothing is added twice.</p>
            </Card>

            <Card title="Partners" sub="Royalties and affiliate commissions: earned, paid, and still owed.">
              <PartnerTable partners={d.partners} busy={busy} run={run} />
            </Card>

            <Card title="Add an entry" sub="Anything else: an expense, money you put in, or money you took out.">
              <EntryForm accounts={accounts} connected={d.connected} busy={busy} run={run} />
            </Card>

            <Card title={`Entries for ${monthName(month)}`} sub={waiting ? `${waiting} waiting to go to the spreadsheet` : 'All in the spreadsheet'}>
              <EntryList entries={d.entries} run={run} />
              <div className="mt-3 flex items-center gap-3">
                <button type="button" className={btn} disabled={!d.connected || !waiting || busy === 'sync'}
                  onClick={() => run('sync', () => api.post('/admin/money/sync'), r => `Sent ${r.data.sent} ${r.data.sent === 1 ? 'entry' : 'entries'} to the spreadsheet.`)}>
                  {busy === 'sync' ? 'Sending…' : 'Send to spreadsheet'}
                </button>
                {!d.connected && <span className="text-xs text-gray-500">Connect Google Sheets first.</span>}
              </div>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function PartnerTable({ partners, busy, run }) {
  const [open, setOpen] = useState(null);
  if (partners.length === 0) return <p className="text-sm text-gray-500">No partners yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-gray-500"><th className="py-1.5">Partner</th><th>Type</th><th className="text-right">Earned this month</th><th className="text-right">Paid this month</th><th className="text-right">Owed now</th><th /></tr></thead>
        <tbody>
          {partners.map(p => (
            <React.Fragment key={p.id}>
              <tr className="border-t border-gray-100">
                <td className="py-2 text-reset-brown font-medium">{p.name}</td>
                <td>{p.type === 'royalty' ? 'Royalty' : 'Affiliate'}</td>
                <td className="text-right">{fmt(p.earnedThisMonth)}</td>
                <td className="text-right">{fmt(p.paidThisMonth)}</td>
                <td className="text-right font-semibold">{fmt(p.owedNow)}</td>
                <td className="text-right"><button type="button" className={btnQuiet} onClick={() => setOpen(open === p.id ? null : p.id)}>Record a payout</button></td>
              </tr>
              {open === p.id && (
                <tr><td colSpan={6} className="pb-3"><PayoutForm partner={p} busy={busy} run={run} onDone={() => setOpen(null)} /></td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayoutForm({ partner, busy, run, onDone }) {
  const [amount, setAmount] = useState(partner.owedNow ? String(partner.owedNow) : '');
  const [date, setDate] = useState(today());
  const [paidFrom, setPaidFrom] = useState('personal');
  const [note, setNote] = useState('');
  const submit = (e) => {
    e.preventDefault();
    run('payout', () => api.post('/admin/money/payouts', { partnerId: partner.id, amount, date, paidFrom, note }),
      () => `Recorded a payout of ${fmt(amount)} to ${partner.name}.`).then(onDone);
  };
  return (
    <form onSubmit={submit} className="bg-reset-cream rounded-xl p-3 grid sm:grid-cols-4 gap-2 items-end">
      <div><label className={label} htmlFor={`amt-${partner.id}`}>Amount sent</label><input id={`amt-${partner.id}`} className={input} type="number" step="0.01" min="0.01" value={amount} onChange={e => setAmount(e.target.value)} required /></div>
      <div><label className={label} htmlFor={`dt-${partner.id}`}>Date sent</label><input id={`dt-${partner.id}`} className={input} type="date" value={date} onChange={e => setDate(e.target.value)} required /></div>
      <div><label className={label} htmlFor={`pf-${partner.id}`}>Sent from</label><select id={`pf-${partner.id}`} className={input} value={paidFrom} onChange={e => setPaidFrom(e.target.value)}>{PAID_FROM.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
      <div><label className={label} htmlFor={`nt-${partner.id}`}>Note (optional)</label><input id={`nt-${partner.id}`} className={input} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. e-transfer, September" /></div>
      <div className="sm:col-span-4"><button type="submit" className={btn} disabled={busy === 'payout'}>{busy === 'payout' ? 'Saving…' : 'Save payout'}</button></div>
    </form>
  );
}

function EntryForm({ accounts, connected, busy, run }) {
  const [direction, setDirection] = useState('out');
  const [date, setDate] = useState(today());
  const [description, setDescription] = useState('');
  const [payee, setPayee] = useState('');
  const [account, setAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [paidFrom, setPaidFrom] = useState('personal');
  if (!connected) return <p className="text-sm text-gray-500">Connect Google Sheets first, so the account list matches your spreadsheet.</p>;
  if (!accounts) return <p className="text-sm text-gray-500">Loading your accounts…</p>;
  const choices = direction === 'out'
    ? accounts.filter(a => a.type === 'Expense' || a.number === 3100)
    : accounts.filter(a => a.type === 'Revenue' || a.number === 3000);
  const groups = [...new Set(choices.map(a => a.group))];
  const submit = (e) => {
    e.preventDefault();
    run('entry', () => api.post('/admin/money/entries', { direction, date, description, payee, account, amount, paidFrom }),
      () => 'Entry saved. It will go to the spreadsheet when you press Send.').then(() => { setDescription(''); setPayee(''); setAmount(''); });
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2 flex gap-1 bg-gray-100 rounded-lg p-1 text-sm font-medium w-fit">
        {[['out', 'Money out'], ['in', 'Money in']].map(([v, l]) => (
          <button key={v} type="button" onClick={() => { setDirection(v); setAccount(''); setPaidFrom('personal'); }}
            className={`px-3 py-1.5 rounded-md ${direction === v ? 'bg-white text-reset-brown shadow-sm' : 'text-gray-500'}`}>{l}</button>
        ))}
      </div>
      <div><label className={label} htmlFor="e-date">Date</label><input id="e-date" className={input} type="date" value={date} onChange={e => setDate(e.target.value)} required /></div>
      <div><label className={label} htmlFor="e-amt">Amount (CA$)</label><input id="e-amt" className={input} type="number" step="0.01" min="0.01" value={amount} onChange={e => setAmount(e.target.value)} required /></div>
      <div><label className={label} htmlFor="e-desc">What it was for</label><input id="e-desc" className={input} value={description} onChange={e => setDescription(e.target.value)} required placeholder={direction === 'out' ? 'e.g. Zoom Pro, October' : 'e.g. Money I put in'} /></div>
      <div><label className={label} htmlFor="e-payee">{direction === 'out' ? 'Paid to' : 'Received from'}</label><input id="e-payee" className={input} value={payee} onChange={e => setPayee(e.target.value)} placeholder="e.g. Zoom" /></div>
      <div><label className={label} htmlFor="e-acct">Account</label>
        <select id="e-acct" className={input} value={account} onChange={e => setAccount(e.target.value)} required>
          <option value="">Choose…</option>
          {groups.map(g => <optgroup key={g} label={g}>{choices.filter(a => a.group === g).map(a => <option key={a.number} value={a.number}>{a.name}</option>)}</optgroup>)}
        </select></div>
      <div><label className={label} htmlFor="e-from">{direction === 'out' ? 'Paid from' : 'Went into'}</label>
        <select id="e-from" className={input} value={paidFrom} onChange={e => setPaidFrom(e.target.value)}>{PAID_FROM.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
      <div className="sm:col-span-2"><button type="submit" className={btn} disabled={busy === 'entry'}>{busy === 'entry' ? 'Saving…' : 'Save entry'}</button></div>
    </form>
  );
}

function EntryList({ entries, run }) {
  if (entries.length === 0) return <p className="text-sm text-gray-500">No entries this month yet.</p>;
  const total = (e, side) => e.lines.reduce((a, l) => a + Number(l[side] || 0), 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-gray-500"><th className="py-1.5">Date</th><th>What</th><th>Who</th><th className="text-right">Amount</th><th>Status</th><th /></tr></thead>
        <tbody>
          {entries.map(e => (
            <tr key={e.id} className="border-t border-gray-100">
              <td className="py-2 whitespace-nowrap">{String(e.entry_date).slice(0, 10)}</td>
              <td>{e.description}</td>
              <td>{e.payee || ''}</td>
              <td className="text-right">{fmt(total(e, 'debit'))}</td>
              <td className="whitespace-nowrap">{e.synced_at ? <span className="text-reset-sage">In spreadsheet</span> : <span className="text-gray-500">Waiting</span>}</td>
              <td className="text-right">{!e.synced_at && <button type="button" className="text-xs text-gray-500 underline" onClick={() => run('del', () => api.delete(`/admin/money/entries/${e.id}`), () => 'Entry removed.')}>Remove</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
